import React, { useEffect, useState } from 'react';
import Modal from '../Common/Modal';
import { outlineApi } from '../../api/outlines';
import { workshopsApi } from '../../api/workshops';
import './ConnectSceneModal.css';

/**
 * ConnectSceneModal — the Connect action for Focus/Scene sessions
 * (plot-dossier-spec.md, "Focus/Scene sessions"). Two branches, one modal:
 *
 * - Link to an existing scene: reuses the pre-existing, generic
 *   POST /sessions/{id}/link-section (outlineApi.linkSession) unchanged —
 *   it already supports many sessions linking to one scene, additively.
 * - Create a new scene: POST /sessions/{id}/connect-new-scene
 *   (outlineApi.connectNewScene) — lands the scene "Unplaced"
 *   (parent_section_id=null); the writer places it later on PlotTrack.
 *
 * Only rendered when the caller has already confirmed the story has an
 * outline (the Connect button itself is disabled otherwise, per spec) —
 * this modal does not re-check that, but does surface a plain error if the
 * "create new scene" call 400s for any reason.
 */
export default function ConnectSceneModal({ isOpen, onClose, storyId, sessionId, onConnected }) {
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [scenes, setScenes] = useState([]);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [selectedSceneId, setSelectedSceneId] = useState('');
  const [newSceneName, setNewSceneName] = useState('');
  const [newSceneDescription, setNewSceneDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !storyId) return;
    setMode('existing');
    setSelectedSceneId('');
    setNewSceneName('');
    setNewSceneDescription('');
    setError(null);

    let cancelled = false;
    const loadScenes = async () => {
      setScenesLoading(true);
      try {
        const response = await workshopsApi.getOutlineMap(storyId);
        const data = response?.data || response;
        const sections = data?.sections || [];
        const sceneSections = sections
          .filter((s) => s.section_type === 'scene')
          .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
        if (!cancelled) {
          setScenes(sceneSections);
          if (sceneSections.length === 0) setMode('new');
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load the story’s scenes.');
      } finally {
        if (!cancelled) setScenesLoading(false);
      }
    };
    loadScenes();
    return () => { cancelled = true; };
  }, [isOpen, storyId]);

  const handleSubmit = async () => {
    setError(null);
    if (mode === 'existing') {
      if (!selectedSceneId) {
        setError('Pick a scene first.');
        return;
      }
      setIsSubmitting(true);
      try {
        await outlineApi.linkSession(sessionId, selectedSceneId);
        onConnected?.();
      } catch (err) {
        setError(err?.message || 'Could not link this session to that scene.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const trimmedName = newSceneName.trim();
      if (!trimmedName) {
        setError('Name the new scene first.');
        return;
      }
      setIsSubmitting(true);
      try {
        await outlineApi.connectNewScene(sessionId, trimmedName, newSceneDescription.trim() || null);
        onConnected?.();
      } catch (err) {
        setError(err?.message || 'Could not create a new scene from this session.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect to Plot Grid">
      <div className="connect-scene-modal">
        <p className="connect-scene-modal__intro">
          Link this session's content to a scene — or hand it off as the start of a new one.
        </p>

        <div className="connect-scene-modal__mode-picker" role="radiogroup" aria-label="Connect mode">
          <button
            type="button"
            className={`connect-scene-modal__mode-btn${mode === 'existing' ? ' connect-scene-modal__mode-btn--active' : ''}`}
            onClick={() => setMode('existing')}
            disabled={scenes.length === 0}
            aria-pressed={mode === 'existing'}
          >
            Link to an existing scene
          </button>
          <button
            type="button"
            className={`connect-scene-modal__mode-btn${mode === 'new' ? ' connect-scene-modal__mode-btn--active' : ''}`}
            onClick={() => setMode('new')}
            aria-pressed={mode === 'new'}
          >
            Create a new scene
          </button>
        </div>

        {error && <div className="connect-scene-modal__error">{error}</div>}

        {mode === 'existing' ? (
          scenesLoading ? (
            <p className="connect-scene-modal__hint">Loading scenes…</p>
          ) : scenes.length === 0 ? (
            <p className="connect-scene-modal__hint">
              No scenes exist yet on Plot Grid — create a new one instead.
            </p>
          ) : (
            <select
              className="connect-scene-modal__select"
              value={selectedSceneId}
              onChange={(e) => setSelectedSceneId(e.target.value)}
              aria-label="Existing scene"
            >
              <option value="">Choose a scene…</option>
              {scenes.map((scene) => (
                <option key={scene.id} value={scene.id}>
                  {scene.display_name || 'Untitled scene'}
                  {scene.parent_section_id ? '' : ' (Unplaced)'}
                </option>
              ))}
            </select>
          )
        ) : (
          <div className="connect-scene-modal__new-scene">
            <label className="connect-scene-modal__label" htmlFor="connect-scene-name">
              Scene name
            </label>
            <input
              id="connect-scene-name"
              type="text"
              className="connect-scene-modal__input"
              value={newSceneName}
              onChange={(e) => setNewSceneName(e.target.value)}
              placeholder="e.g. The Docks at Midnight"
              autoFocus
            />
            <label className="connect-scene-modal__label" htmlFor="connect-scene-description">
              Description (optional)
            </label>
            <textarea
              id="connect-scene-description"
              className="connect-scene-modal__textarea"
              value={newSceneDescription}
              onChange={(e) => setNewSceneDescription(e.target.value)}
              placeholder="A short note for later reference"
              rows={3}
            />
            <p className="connect-scene-modal__hint">
              Lands Unplaced on Plot Grid — place it onto a sequence there whenever you're ready.
            </p>
          </div>
        )}

        <div className="connect-scene-modal__actions">
          <button
            type="button"
            className="connect-scene-modal__btn connect-scene-modal__btn--secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="connect-scene-modal__btn connect-scene-modal__btn--primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
